import React, { useEffect, useRef, useState } from 'react';
import { IMortgage } from '../../../../Interfaces/IMortgage';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';

interface IProps {
  mortgageObject: IMortgage;
  setMortgageObject: Function;
}

const transcriptOptions = [
  'simple_transcription',
  'certified_copy',
  'simple_execution',
  'enforceable_copy',
] as const;

type TranscriptOption = (typeof transcriptOptions)[number];

export const MortgageDetails: React.FC<IProps> = ({
  mortgageObject,
  setMortgageObject,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const initialCheckedOptions: Record<TranscriptOption, boolean> = {
    simple_transcription:
      mortgageObject.Transcripts?.includes('simple_transcription') || false,
    certified_copy:
      mortgageObject.Transcripts?.includes('certified_copy') || false,
    simple_execution:
      mortgageObject.Transcripts?.includes('simple_execution') || false,
    enforceable_copy:
      mortgageObject.Transcripts?.includes('enforceable_copy') || false,
  };

  const [checkedOptions, setCheckedOptions] = useState(initialCheckedOptions);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedOptions({
      ...checkedOptions,
      [event.target.name as TranscriptOption]: event.target.checked,
    });
  };
  const getLabelForOption = (option: TranscriptOption) => {
    switch (option) {
      case 'simple_transcription':
        return 'Einfache Abschrift';
      case 'certified_copy':
        return 'Beglaubigte Abschrift';
      case 'simple_execution':
        return 'Einfache Ausfertigung';
      case 'enforceable_copy':
        return 'Vollstreckbare Ausfertigung';
      default:
        return '';
    }
  };

  useEffect(() => {
    const selectedTranscripts = transcriptOptions.filter(
      option => checkedOptions[option]
    );

    setMortgageObject({
      ...mortgageObject,
      Transcripts: selectedTranscripts,
    });
  }, [checkedOptions]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography>Neue/ zu löschende Grundschuld</Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl component='fieldset' size='small'>
            <RadioGroup
              row
              aria-label='for-who'
              name='who'
              value={mortgageObject.ToBeDeleted ? 'delete' : 'new'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value === 'delete';
                if (newValue === true) {
                  setMortgageObject({
                    ...mortgageObject,
                    ToBeDeleted: true,
                    BeneficiaryType: null,
                    PropertyForBuying: null,
                    PropertyOwned: null,
                    DocumentIndexNumber: '',
                    OperationNumber: '',
                  });
                } else {
                  setMortgageObject({
                    ...mortgageObject,
                    ToBeDeleted: false,
                  });
                }
              }}
            >
              <FormControlLabel
                value='delete'
                control={<Radio required size='small' />}
                label='Zu löschen'
              />
              <FormControlLabel
                value='new'
                control={<Radio required size='small' />}
                label='Neu'
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {!mortgageObject.ToBeDeleted && (
          <>
            <Grid mt={2} item xs={12}>
              <Typography>
                Für wen soll die Grundschuld bestellt werden?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl component='fieldset' size='small'>
                <RadioGroup
                  aria-label='for-who'
                  name='who'
                  value={mortgageObject.BeneficiaryType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setMortgageObject({
                      ...mortgageObject,
                      BeneficiaryType: event.target.value,
                    })
                  }
                >
                  <FormControlLabel
                    value='INSTITUTION'
                    control={<Radio required size='small' />}
                    label='Eine Bank (auch [Bau-] Sparkasse) oder eine Versicherung'
                  />
                  <FormControlLabel
                    value='PRIVATE'
                    control={<Radio required size='small' />}
                    label='Eine Privatperson oder ein sonstiges Unternehmen'
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid mt={2} item xs={12}>
              <Typography>
                Wird die Grundschuld zur Finanzierung eines Immobilienkaufs
                benötigt?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl component='fieldset' size='small'>
                <RadioGroup
                  row
                  aria-label='property'
                  name='property-buy'
                  value={
                    mortgageObject.PropertyForBuying === null
                      ? null
                      : mortgageObject.PropertyForBuying
                        ? 'yes'
                        : 'no'
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.target.value === 'yes') {
                      setMortgageObject({
                        ...mortgageObject,
                        PropertyForBuying: true,
                      });
                    } else {
                      setMortgageObject({
                        ...mortgageObject,
                        PropertyForBuying: false,
                        PropertyOwned: null,
                        DocumentIndexNumber: '',
                        OperationNumber: '',
                      });
                    }
                  }}
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio required size='small' />}
                    label='Ja'
                  />
                  <FormControlLabel
                    value='no'
                    control={<Radio required size='small' />}
                    label='Nein'
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {mortgageObject.PropertyForBuying && (
              <>
                <Grid mt={2} item xs={12}>
                  <Typography>
                    Ist der Immobilienkauf bereits erfolgt?
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component='fieldset' size='small'>
                    <RadioGroup
                      row
                      aria-label='property-owned'
                      name='property-owned'
                      value={
                        mortgageObject.PropertyOwned === null ||
                        mortgageObject.PropertyForBuying === undefined
                          ? ''
                          : mortgageObject.PropertyOwned
                            ? 'yes'
                            : 'no'
                      }
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        if (event.target.value === 'yes') {
                          setMortgageObject({
                            ...mortgageObject,
                            PropertyOwned: true,
                          });
                        } else {
                          setMortgageObject({
                            ...mortgageObject,
                            PropertyOwned: false,
                            DocumentIndexNumber: '',
                            OperationNumber: '',
                          });
                        }
                      }}
                    >
                      <FormControlLabel
                        value='yes'
                        control={<Radio required size='small' />}
                        label='Ja'
                      />
                      <FormControlLabel
                        value='no'
                        control={<Radio required size='small' />}
                        label='Nein'
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {mortgageObject.PropertyOwned && (
                  <>
                    <Grid mt={2} item xs={12}>
                      {' '}
                      <Typography>
                        Geben Sie die Urkundenverzeichnisnummer ein (sog.
                        UVZ-Nummer)?
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label='Urkundenverzeichnisnummer'
                        size='small'
                        fullWidth
                        value={mortgageObject.DocumentIndexNumber}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          setMortgageObject({
                            ...mortgageObject,
                            DocumentIndexNumber: event.target.value,
                          })
                        }
                        required
                      />
                    </Grid>

                    <Grid mt={2} item xs={12}>
                      <Typography>
                        Geben Sie die Vorgangsnummer (sechsstellig, befindet
                        sich ausgegraut auf der ersten Seite des Entwurfs) an.
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label='Vorgangsnummer'
                        size='small'
                        fullWidth
                        value={mortgageObject.OperationNumber}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          setMortgageObject({
                            ...mortgageObject,
                            OperationNumber: event.target.value,
                          })
                        }
                        required
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid mt={2} item xs={12}>
              <Typography>
                Über welchen Betrag soll die Grundschuld bestellt werden?
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <NumericFormat
                label='Betrag der Grundschuld'
                size='small'
                suffix='€'
                fixedDecimalScale
                decimalScale={2}
                thousandsGroupStyle='thousand'
                thousandSeparator='.'
                decimalSeparator=','
                fullWidth
                required
                value={mortgageObject.AmountMortgage}
                customInput={TextField}
                inputRef={inputRef}
                onValueChange={values => {
                  const { floatValue } = values;
                  const cursorPosition = inputRef.current?.selectionStart;
                  setMortgageObject({
                    ...mortgageObject,
                    AmountMortgage:
                      floatValue !== undefined ? floatValue : null,
                  });
                  setTimeout(() => {
                    if (inputRef.current && cursorPosition) {
                      inputRef.current.setSelectionRange(
                        cursorPosition,
                        cursorPosition
                      );
                    }
                  }, 0);
                }}
                allowNegative={false}
              />
            </Grid>

            <Grid mt={2} item xs={12}>
              <Typography>
                Wie hoch sind die sog. Grundschuldzinsen (in %)?
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <NumericFormat
                value={mortgageObject.MortgageInterest}
                suffix='%'
                decimalSeparator='.'
                label='Grundschuldzinsen'
                size='small'
                fullWidth
                required
                customInput={TextField}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                isAllowed={values => {
                  const { floatValue } = values;

                  if (floatValue && floatValue >= 100) {
                    return false;
                  }
                  return true;
                }}
                onValueChange={values => {
                  const { floatValue } = values;
                  setMortgageObject({
                    ...mortgageObject,
                    MortgageInterest:
                      floatValue !== undefined ? floatValue : null,
                  });
                }}
              />
            </Grid>
            <Grid mt={2} item xs={12}>
              <Typography>Gibt es Nebenleistungen?</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                rows={3}
                multiline
                label='Nebenleistungen'
                size='small'
                fullWidth
                value={mortgageObject.AdditionalServices}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setMortgageObject({
                    ...mortgageObject,
                    AdditionalServices: event.target.value,
                  })
                }
              />
            </Grid>

            <Grid mt={2} item xs={12}>
              <Typography>
                Welche Abschriften soll der Gläubiger erhalten?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormGroup>
                {transcriptOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={checkedOptions[option]}
                        onChange={handleChange}
                        name={option}
                      />
                    }
                    label={getLabelForOption(option)}
                  />
                ))}
              </FormGroup>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};
